import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';

import { useBankingDataContext } from '#client/BankingDataContext';
import { ASTRA_STATUS, CARD_STATUS, CARD_TYPE, ID_THEFT_SCORE } from '#client/constants/astra';
import { RELEASE_SHOW_INSTANT_DEPOSIT } from '#client/constants/experimentsFlags';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useConstBusinessEntityUuid from '#client/hooks/useConstBusinessEntityUuid';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import useToastContext from '#client/hooks/useToastContext';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { SYNC_CARD_MUTATION } from '#client/mutations';

import useFeatureFlag from './useFeatureFlag';

export const AstraProfileContext = createContext();
AstraProfileContext.displayName = 'AstraProfileContext';

const useAstraProfileContext = () => {
  const context = useContext(AstraProfileContext);

  if (typeof context === 'undefined') {
    throw new Error('useAstraProfileContext must be used within a AstraProfileContext.Provider');
  }

  return context;
};

/**
 * Get the status of the first instant funding account if available
 * @param {Array} accounts - List of instant funding accounts
 * @returns {string|null} - Status of the first account if available, otherwise null
 */
const getFirstAccountStatus = (accounts) => {
  if (!accounts || accounts.length === 0) {
    return null;
  }

  return accounts[0].accountStatus.toLowerCase();
};

export const AstraProfileProvider = ({ children }) => {
  const { isFetchingMfaStatus } = useUnitAccountId();
  const [isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading] = useFeatureFlag(
    RELEASE_SHOW_INSTANT_DEPOSIT
  );

  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const { createCommonErrorToast } = useToastContext();
  const { idTheftScore } = useApplicationStatusContext();

  const {
    error: astraProfileError,
    loading,
    data: originalAstraProfileData,
    errors: astraProfileErrors,
    refetch: refreshAstraProfile
  } = useBankingDataContext();

  useLogError(astraProfileErrors, astraProfileError);

  const astraProfileData = useMemoObject(originalAstraProfileData);

  useEffect(() => {
    if (astraProfileError) {
      createCommonErrorToast({ error: astraProfileError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [astraProfileError]);

  const [syncCard] = useMutation(SYNC_CARD_MUTATION, {
    noThrow: true
  });

  const syncAndRefetch = useCallback(async () => {
    const { error: syncError } = await syncCard({
      variables: {
        businessEntityUuid
      }
    });

    if (syncError) {
      createCommonErrorToast({ error: syncError });
      return;
    }

    await refreshAstraProfile();
  }, [businessEntityUuid, createCommonErrorToast, refreshAstraProfile, syncCard]);

  const astraProfileCardList = useMemo(
    () =>
      (astraProfileData?.instantFundingAccounts?.[0]?.instantFundingCards ?? []).filter(
        (card) => card.isSourceCard && !card.isInstantFundingCardRemoved
      ),
    [astraProfileData]
  );

  const accountStatus = getFirstAccountStatus(astraProfileData?.instantFundingAccounts);

  /**
   * The destination card need to be created destination card if:
   * 1. Has astra profile
   * 2. Has original card list and all cards in the card list have the prop isSourceCard = true
   * 3. Or the original list do not has any card
   */
  const isExistedAstraProfile = !loading && astraProfileData?.instantFundingAccounts?.length > 0;
  const hasCardWithNoSource =
    isExistedAstraProfile &&
    (astraProfileData?.instantFundingAccounts?.[0]?.instantFundingCards ?? []).some(
      (card) => !card.isSourceCard && !card.isInstantFundingCardRemoved
    );

  const isNeedCreateDestinationCard = !loading && !hasCardWithNoSource;

  const cardList = astraProfileCardList?.map((card) => {
    const { cardStatus, cardType, cardReviewStatus, isCardPullEnabled } = card;
    return {
      ...card,
      isValidCard:
        cardType === CARD_TYPE.DEBIT &&
        cardStatus === CARD_STATUS.APPROVED &&
        cardReviewStatus === CARD_STATUS.APPROVED &&
        isCardPullEnabled
    };
  });

  // Check if the first instant funding account in astraProfileData is eligible for transfers
  const isEligibleForTransfers =
    astraProfileData?.instantFundingAccounts?.[0]?.isEligibleForTransfers;

  // The account status is considered valid if it is either 'ACTIVE' or 'SUSPENDED'
  const isValidAccountStatus = [ASTRA_STATUS.ACTIVE, ASTRA_STATUS.SUSPENDED].includes(
    accountStatus
  );

  const isHideInstantDepositOption = useMemo(() => {
    // Early return if the primary condition is not met
    if (!isShowInstantDepositFlagLoading && !isShowInstantDepositFlagEnabled) {
      return true;
    }

    // Check the Astra Business Profile flag and ID theft score
    return idTheftScore >= ID_THEFT_SCORE.CUTOFF_LIMIT;
  }, [idTheftScore, isShowInstantDepositFlagEnabled, isShowInstantDepositFlagLoading]);

  /**
   * If both conditions are met, decide whether to attempt creating a new Astra profile.
   * 1. No existing Astra profile is found (`isExistedAstraProfile` is false), OR
   * 2. The current account status is not valid (`isValidAccountStatus` is false).
   */
  const shouldAttemptCreateAstraProfile = useMemo(
    () => !isExistedAstraProfile || !isValidAccountStatus,
    [isExistedAstraProfile, isValidAccountStatus]
  );

  const value = useMemo(
    () => ({
      isExistedAstraProfile,
      hasAcceptedTerms: !!astraProfileData?.instantFundingAccounts?.[0]?.hasAcceptedTerms,
      isEligibleForTransfers,
      accountStatus,
      isValidAccountStatus,
      isNeedCreateDestinationCard,
      shouldAttemptCreateAstraProfile,
      isHideInstantDepositOption,
      cardList,
      loading: loading || isFetchingMfaStatus,
      error: astraProfileError,
      refreshAstraProfile,
      syncAndRefetch
    }),
    [
      isExistedAstraProfile,
      accountStatus,
      astraProfileData?.instantFundingAccounts,
      isEligibleForTransfers,
      isValidAccountStatus,
      shouldAttemptCreateAstraProfile,
      isHideInstantDepositOption,
      astraProfileError,
      cardList,
      isNeedCreateDestinationCard,
      loading,
      refreshAstraProfile,
      syncAndRefetch,
      isFetchingMfaStatus
    ]
  );

  return <AstraProfileContext.Provider value={value}>{children}</AstraProfileContext.Provider>;
};

export default useAstraProfileContext;
